import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { g, p, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Flame, Clock, Hand, Bike, ShoppingBag, RotateCcw, Phone } from "lucide-react";

const mmss = (s) => `${Math.floor(s / 60)}′${String(s % 60).padStart(2, "0")}`;

const Prendi = ({ c, user, load }) => (
  <Button
    size="sm"
    data-testid={`ritiro-claim-${c.key}`}
    onClick={async () => {
      try {
        await p(`/pickups/${encodeURIComponent(c.key)}/claim`, { by: user.name });
      } catch (e) {
        toast.error(errMsg(e));
      }
      load();
    }}
  >
    <Hand className="mr-1 h-3.5 w-3.5" /> Ci vado io
  </Button>
);

/** Card di un giro da portare al tavolo. */
const CardTavolo = ({ c, user, load, nav }) => (
  <Card
    className={`p-4 ${c.late ? "border-2 border-rose-400 bg-rose-50" : c.claimed_by ? "opacity-80" : ""}`}
    data-testid={`ritiro-${c.key}`}
  >
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="font-bold">
          Tavolo {c.tables}
          <span className="ml-2 rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-bold text-white">
            {c.single ? "subito" : `giro ${c.giro}`}
          </span>
          {c.hot && <Flame className="ml-1.5 inline h-4 w-4 text-orange-500" />}
        </p>
        <p className="text-[11px] text-slate-500">
          <Clock className="mr-0.5 inline h-3 w-3" />
          pronto da {mmss(c.waited_seconds)}
          {c.zone && ` · ${c.zone}`}
          {c.suggested && ` · comanda di ${c.suggested}`}
        </p>
        {c.recalled > 0 && (
          <p className="text-[11px] font-semibold text-amber-700" data-testid={`ritiro-richiamato-${c.key}`}>
            <RotateCcw className="mr-0.5 inline h-3 w-3" /> rifatto in cucina {c.recalled}×
          </p>
        )}
      </div>
      {c.late && (
        <span className="rounded-full bg-rose-600 px-2 py-1 text-[10px] font-bold text-white" data-testid={`ritiro-tardi-${c.key}`}>
          URGENTE
        </span>
      )}
    </div>

    <ul className="mt-2 space-y-0.5 text-sm">
      {c.items.map((i) => (
        <li key={i.id} className="truncate">
          <span className="font-num font-bold">{i.qty}×</span> {i.name}
          {i.note && <span className="text-amber-700"> · {i.note}</span>}
        </li>
      ))}
    </ul>

    <div className="mt-3 flex flex-wrap gap-2">
      {c.claimed_by ? (
        <span className="rounded-full bg-emerald-100 px-2.5 py-1.5 text-[11px] font-bold text-emerald-900" data-testid={`ritiro-preso-${c.key}`}>
          ci va {c.claimed_by}
        </span>
      ) : (
        <Prendi c={c} user={user} load={load} />
      )}
      <Button
        size="sm"
        variant="outline"
        data-testid={`ritiro-served-${c.key}`}
        onClick={async () => {
          await p(`/pickups/${encodeURIComponent(c.key)}/served`, { item_ids: c.item_ids, by: user.name, tables: c.tables });
          toast.success(`Tavolo ${c.tables} servito`);
          load();
        }}
      >
        Portato al tavolo
      </Button>
      <Button size="sm" variant="ghost" className="text-xs" data-testid={`ritiro-comanda-${c.key}`} onClick={() => nav(`/comanda/${c.order_id}`)}>
        Apri comanda
      </Button>
    </div>
  </Card>
);

/** Card di un pacco d'asporto: qui conta l'orario concordato col cliente. */
const CardAsporto = ({ c, user, load, nav }) => {
  const inRitardo = c.due_minutes !== null && c.due_minutes < 0;
  return (
    <Card
      className={`p-4 ${c.late ? "border-2 border-rose-400 bg-rose-50" : c.claimed_by ? "opacity-80" : ""}`}
      data-testid={`asporto-${c.key}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-bold">
            {c.delivery ? <Bike className="mr-1 inline h-4 w-4 text-sky-600" /> : <ShoppingBag className="mr-1 inline h-4 w-4 text-slate-500" />}
            {c.label}
            {c.channel && <span className="ml-2 rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-bold text-white">{c.channel}</span>}
          </p>
          <p className="text-[11px] text-slate-500">
            <Clock className="mr-0.5 inline h-3 w-3" />
            {c.pickup_time
              ? inRitardo
                ? `in ritardo di ${Math.abs(c.due_minutes)}′ sull'orario ${c.pickup_time}`
                : `da consegnare alle ${c.pickup_time} · fra ${c.due_minutes}′`
              : `pronto da ${mmss(c.waited_seconds)}`}
          </p>
          <p className="truncate text-[11px] text-slate-500">
            {c.customer_name}
            {c.rider && ` · rider ${c.rider}`}
            {c.address && ` · ${c.address}`}
          </p>
          {c.recalled > 0 && (
            <p className="text-[11px] font-semibold text-amber-700" data-testid={`asporto-richiamato-${c.key}`}>
              <RotateCcw className="mr-0.5 inline h-3 w-3" /> rifatto in cucina {c.recalled}×
            </p>
          )}
        </div>
        {c.late && (
          <span className="rounded-full bg-rose-600 px-2 py-1 text-[10px] font-bold text-white" data-testid={`asporto-tardi-${c.key}`}>
            IN RITARDO
          </span>
        )}
      </div>

      <ul className="mt-2 space-y-0.5 text-sm">
        {c.items.map((i) => (
          <li key={i.id} className="truncate">
            <span className="font-num font-bold">{i.qty}×</span> {i.name}
            {i.note && <span className="text-amber-700"> · {i.note}</span>}
          </li>
        ))}
      </ul>

      <div className="mt-3 flex flex-wrap gap-2">
        {c.claimed_by ? (
          <span className="rounded-full bg-emerald-100 px-2.5 py-1.5 text-[11px] font-bold text-emerald-900" data-testid={`asporto-preso-${c.key}`}>
            ci pensa {c.claimed_by}
          </span>
        ) : (
          <Prendi c={c} user={user} load={load} />
        )}
        <Button
          size="sm"
          variant="outline"
          data-testid={`asporto-consegnato-${c.key}`}
          onClick={async () => {
            await p(`/pickups/${encodeURIComponent(c.key)}/served`, {
              item_ids: c.item_ids,
              by: user.name,
              tables: c.label,
              delivery_status: c.delivery ? "consegnato" : "ritirato",
            });
            toast.success(c.delivery ? `${c.label} affidato al rider` : `${c.label} ritirato dal cliente`);
            load();
          }}
        >
          {c.delivery ? "Affidato al rider" : "Ritirato dal cliente"}
        </Button>
        {c.phone && (
          <a href={`tel:${c.phone}`} data-testid={`asporto-chiama-${c.key}`}>
            <Button size="sm" variant="ghost" className="text-xs">
              <Phone className="mr-1 h-3.5 w-3.5" /> Chiama
            </Button>
          </a>
        )}
        <Button size="sm" variant="ghost" className="text-xs" data-testid={`asporto-comanda-${c.key}`} onClick={() => nav(`/comanda/${c.order_id}`)}>
          Apri comanda
        </Button>
      </div>
    </Card>
  );
};

/** Due cassetti separati: i giri da portare ai tavoli e i pacchi d'asporto da consegnare. */
export default function Ritiri() {
  const { user } = useApp();
  const nav = useNavigate();
  const [d, setD] = useState({ cards: [], takeaway: [], load: {}, timeout: 90 });

  const load = useCallback(async () => {
    const r = await g("/pickups", { person: user?.name || "" });
    setD({ cards: [], takeaway: [], load: {}, timeout: 90, ...r });
  }, [user?.name]);

  useEffect(() => {
    load();
    const i = setInterval(load, 5000);
    return () => clearInterval(i);
  }, [load]);

  const miei = [...d.cards, ...d.takeaway].filter((c) => c.claimed_by === user?.name).length;

  return (
    <div className="fade-up space-y-6" data-testid="ritiri-page">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500">Sala</p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">Da portare</h1>
        <p className="mt-1 text-sm text-slate-500" data-testid="ritiri-riepilogo">
          {d.cards.length} ai tavoli · {d.takeaway.length} d'asporto · tu ne hai {miei} in mano
        </p>
      </div>

      {Object.keys(d.load).length > 0 && (
        <Card className="flex flex-wrap gap-2 p-3" data-testid="carico-sala">
          {Object.entries(d.load).map(([nome, n]) => (
            <span key={nome} className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600" data-testid={`carico-${nome}`}>
              {nome}: {n} da servire
            </span>
          ))}
        </Card>
      )}

      <section data-testid="cassetto-tavoli">
        <h2 className="text-base font-semibold text-slate-700 md:text-lg">Ai tavoli</h2>
        {d.cards.length === 0 ? (
          <p className="mt-1 text-sm text-slate-500">Nessun piatto in attesa di ritiro.</p>
        ) : (
          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {d.cards.map((c) => (
              <CardTavolo key={c.key} c={c} user={user} load={load} nav={nav} />
            ))}
          </div>
        )}
      </section>

      <section data-testid="cassetto-asporto">
        <h2 className="text-base font-semibold text-slate-700 md:text-lg">Asporto e consegne</h2>
        {d.takeaway.length === 0 ? (
          <p className="mt-1 text-sm text-slate-500">Nessun pacco pronto da consegnare.</p>
        ) : (
          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {d.takeaway.map((c) => (
              <CardAsporto key={c.key} c={c} user={user} load={load} nav={nav} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
